import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";
import { Inbox, Loader2, X, CheckCircle2, XCircle, Save, Mail, Phone, Calendar, MapPin } from "lucide-react";
import { saveSuccess, saveError } from "../../lib/saveBanner";
import { isoToEU } from "../../lib/format";

/**
 * Admin Inquiry Inbox — v3.8.7.
 *
 * Every `POST /public/inquiry` from an "Upon Request" Car Group lands
 * here as a booking with `status=inquiry_request`. Staff can:
 *   • Convert → set a quoted total; booking flips to `pending_payment`
 *     and enters the normal cashflow (payment link, agreement, etc.).
 *   • Decline → soft-mark as `declined` so the record stays for audit
 *     but disappears from the default view.
 */
export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeDeclined, setIncludeDeclined] = useState(false);
  const [converting, setConverting] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/inquiries", {
        params: { include_declined: includeDeclined },
      });
      setItems(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      saveError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [includeDeclined]);
  useEffect(() => { load(); }, [load]);

  const decline = async (inq) => {
    const ok = await confirm({
      title:        "Decline inquiry",
      message:      "The record stays for audit but drops off the default inbox view.",
      subject:      `${inq.full_name} · ${inq.vehicle_snapshot?.name || inq.vehicle_id}`,
      confirmLabel: "Decline inquiry",
      testId:       "confirm-decline-inquiry",
    });
    if (!ok) return;
    try {
      await api.post(`/admin/inquiries/${inq.id}/decline`);
      saveSuccess("Inquiry declined");
      load();
    } catch (e) {
      saveError(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const openInbox = items.filter((i) => i.status === "inquiry_request");
  const declined  = items.filter((i) => i.status === "declined");

  return (
    <div className="max-w-6xl px-6 lg:px-10 pt-6 lg:pt-10 pb-12" data-testid="inquiries-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
            Ops
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
            <Inbox size={28} /> Inquiries
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
            Every <em>Upon Request</em> booking lands here. Price it, convert
            it into a real booking, or decline it — all without hunting through
            the main Bookings list.
          </p>
        </div>
        <label className="text-xs text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeDeclined}
            onChange={(e) => setIncludeDeclined(e.target.checked)}
            className="accent-black dark:accent-white"
            data-testid="toggle-include-declined"
          />
          Show declined
        </label>
      </div>

      <StatRow open={openInbox.length} declined={declined.length} />

      <section className="mt-6 bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden">
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Inbox</p>
          <h2 className="font-display text-xl mt-1">Open inquiries</h2>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10">
              <tr>
                <th className="py-2 px-6">Customer</th>
                <th className="py-2 px-4">Vehicle</th>
                <th className="py-2 px-4">Window</th>
                <th className="py-2 px-4">Contact</th>
                <th className="py-2 px-4">Status</th>
                <th className="py-2 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="py-8 text-center text-neutral-500"><Loader2 size={16} className="mx-auto animate-spin" /></td></tr>}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-14 text-center text-sm text-neutral-500 dark:text-neutral-400" data-testid="inquiries-empty">
                    No inquiries yet. When customers send an <em>Upon Request</em> booking, it will show up here.
                  </td>
                </tr>
              )}
              {items.map((inq) => <InquiryRow key={inq.id} inq={inq} onConvert={() => setConverting(inq)} onDecline={() => decline(inq)} />)}
            </tbody>
          </table>
        </div>
      </section>

      {converting && (
        <ConvertModal
          inquiry={converting}
          onCancel={() => setConverting(null)}
          onDone={() => {
            setConverting(null);
            saveSuccess("Inquiry converted into a booking");
            load();
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
}

function StatRow({ open, declined }) {
  return (
    <div className="grid grid-cols-2 gap-4 mt-2">
      <StatCard label="Open" value={open} accent="amber" testId="stat-open" />
      <StatCard label="Declined" value={declined} accent="rose" testId="stat-declined" />
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  const cls = accent === "amber"
    ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20"
    : "border-rose-400 bg-rose-50 dark:bg-rose-900/20";
  return (
    <div className={`p-4 border-l-4 rounded-r ${cls}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-neutral-600 dark:text-neutral-300">{label}</p>
      <p className="text-2xl font-display mt-1 tabular-nums">{value}</p>
    </div>
  );
}

function InquiryRow({ inq, onConvert, onDecline }) {
  const isOpen = inq.status === "inquiry_request";
  return (
    <tr className="border-b border-black/5 dark:border-white/5" data-testid={`inquiry-row-${inq.id}`}>
      <td className="py-3 px-6">
        <p className="font-medium">{inq.full_name}</p>
        <p className="text-[10px] text-neutral-400">#{inq.id.slice(0, 8)}</p>
      </td>
      <td className="py-3 px-4">
        <p className="text-sm">{inq.vehicle_snapshot?.name || inq.vehicle_id}</p>
        <p className="text-[10px] text-neutral-500">{inq.vehicle_snapshot?.category || ""}</p>
      </td>
      <td className="py-3 px-4">
        <p className="text-sm tabular-nums">
          {isoToEU(inq.pickup_date)} <span className="text-neutral-400">→</span> {isoToEU(inq.return_date)}
        </p>
        <p className="text-[10px] text-neutral-500">{inq.pickup_time} → {inq.return_time}</p>
      </td>
      <td className="py-3 px-4">
        <p className="text-[11px] text-neutral-600 dark:text-neutral-300 inline-flex items-center gap-1"><Mail size={10} /> {inq.user_email}</p>
        <p className="text-[11px] text-neutral-600 dark:text-neutral-300 inline-flex items-center gap-1"><Phone size={10} /> {inq.phone}</p>
      </td>
      <td className="py-3 px-4">
        <span className={`text-[10px] px-2 py-0.5 rounded ${isOpen
          ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
          : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}
        >
          {isOpen ? "Open" : "Declined"}
        </span>
      </td>
      <td className="py-3 px-4 text-right">
        {isOpen ? (
          <div className="inline-flex gap-1">
            <button onClick={onConvert} className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`convert-${inq.id}`}>
              <CheckCircle2 size={12} /> Convert
            </button>
            <button onClick={onDecline} className="text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 inline-flex items-center gap-1" data-testid={`decline-${inq.id}`}>
              <XCircle size={12} /> Decline
            </button>
          </div>
        ) : (
          <span className="text-[11px] text-neutral-400">—</span>
        )}
      </td>
    </tr>
  );
}

function ConvertModal({ inquiry, onCancel, onDone }) {
  const [quoted, setQuoted] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const days = useMemo(() => {
    try {
      const a = new Date(inquiry.pickup_date), b = new Date(inquiry.return_date);
      return Math.max(1, Math.round((b - a) / 86_400_000));
    } catch {
      return 1;
    }
  }, [inquiry.pickup_date, inquiry.return_date]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const total = Number(quoted);
    if (!Number.isFinite(total) || total <= 0) {
      setErr("Quoted total must be a positive number.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/inquiries/${inquiry.id}/convert`, {
        quoted_total: total, notes,
      });
      onDone();
    } catch (e2) {
      const msg = formatApiError(e2?.response?.data?.detail) || e2.message;
      setErr(msg);
      saveError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel} data-testid="convert-modal">
      <div className="bg-white dark:bg-[#1F2937] max-w-lg w-full p-6 rounded border border-black/10 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Convert inquiry</p>
            <h3 className="font-display text-2xl mt-1">{inquiry.full_name}</h3>
            <p className="text-xs text-neutral-500 mt-1 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1"><MapPin size={11} /> {inquiry.pickup_location} → {inquiry.return_location}</span>
              <span className="inline-flex items-center gap-1"><Calendar size={11} /> {isoToEU(inquiry.pickup_date)} → {isoToEU(inquiry.return_date)} · {days}d</span>
            </p>
          </div>
          <button onClick={onCancel} aria-label="Close" data-testid="convert-close"><X size={18} /></button>
        </div>

        {inquiry.notes && (
          <div className="mb-4 p-3 rounded bg-neutral-50 dark:bg-white/5 border border-black/8 dark:border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 dark:text-neutral-400 mb-1">Customer notes</p>
            <p className="text-xs whitespace-pre-wrap">{inquiry.notes}</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Quoted total (€)</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              required
              value={quoted}
              onChange={(e) => setQuoted(e.target.value)}
              className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white text-sm tabular-nums"
              data-testid="convert-quoted"
              autoFocus
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Internal note (optional)</span>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded p-2 outline-none focus:border-black dark:focus:border-white text-sm"
              data-testid="convert-notes"
            />
          </label>
          {err && <p className="text-xs text-rose-600 dark:text-rose-400">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onCancel} className="border border-black/15 dark:border-white/15 px-5 py-2 text-sm rounded hover:border-black dark:hover:border-white" data-testid="convert-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="bg-black text-white dark:bg-white dark:text-black px-5 py-2 text-sm rounded hover:opacity-90 inline-flex items-center gap-1 disabled:opacity-40"
              data-testid="convert-submit"
            >
              <Save size={12} /> {busy ? "Converting…" : "Convert to booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
